import { Text, Tooltip } from '@fluentui/react-components';
import {
    ChevronRight20Regular,
    Home20Regular,
    DocumentFolder20Regular,
    ChatBubblesQuestion20Regular,
    ContactCardRibbon20Regular,
    PeopleSettings20Regular,
    ChevronLeft20Regular,
    Wand20Regular,
    TableSearch20Regular,
    Step16Regular,
    ShieldKeyhole20Regular,
    ListBar20Regular,
    NotepadEdit20Regular,
    GridKanban20Regular,
    ListBarTreeOffset20Regular,
    Sparkle20Regular,
    Apps20Regular,
    PeopleTeam20Regular,
} from '@fluentui/react-icons';
import styles from './index.module.css';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import React from 'react';
import { isProjectFeatureEnabled } from '../../../utils/lensFeatures';
const Sidebar = ({ expandSidebar, setExpandSidebar }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useSelector(state => state.user);
    const { selectedProject } = useSelector(state => state.projectData);
    const [links, setLinks] = React.useState([]);
    const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);
    const moreMenuCloseTimer = React.useRef(null);

    const openMoreMenu = () => {
        if (moreMenuCloseTimer.current) {
            clearTimeout(moreMenuCloseTimer.current);
            moreMenuCloseTimer.current = null;
        }
        setMoreMenuOpen(true);
    };

    const closeMoreMenu = () => {
        moreMenuCloseTimer.current = setTimeout(() => setMoreMenuOpen(false), 120);
    };

    React.useEffect(
        () => () => {
            if (moreMenuCloseTimer.current) {
                clearTimeout(moreMenuCloseTimer.current);
            }
        },
        [],
    );

    // Route to feature key mapping
    const routeToFeatureKey = {
        '/overview': 'project_overview',
        '/maturity-index': 'maturity_index',
        '/security-index': 'security_index',
        '/team-analyser': 'team_analyser',
        '/security-governance': 'security_governance',
        '/dataroom': 'dataroom',
        '/wizard': 'dataroom_wizard',
        '/irl': 'information_requests',
        '/observations': 'observations',
        '/lessons': 'lessons_learnt',
        '/journal': 'journal',
        '/tent': 'the_tent',
        '/tasks': 'project_management',
        '/raid': 'raid_log',
        '/action-log': 'action_log',
        '/software-inventory': 'software_inventory',
        '/hardware-inventory': 'hardware_inventory',
        '/ticket-management': 'ticket_management',
        '/lens-intelligence': 'lens_intelligence',
    };

    // Features that cannot be disabled (always enabled)
    const REQUIRED_FEATURES = ['project_overview', 'dataroom'];

    const moreSidebarItems = React.useMemo(
        () => [
            {
                label: 'Wizard',
                link: '/wizard',
                tooltip: 'Wizard',
                icon: <Wand20Regular />,
            },
            {
                label: 'Maturity Index',
                link: '/maturity-index',
                tooltip: 'Maturity Index',
                icon: (
                    <div style={{ height: 20, width: 20 }}>
                        <Step16Regular />
                    </div>
                ),
            },
            {
                label: 'Security Index',
                link: '/security-index',
                tooltip: 'Security Index',
                icon: <ShieldKeyhole20Regular />,
            },
            {
                label: 'Team Analyser',
                link: '/team-analyser',
                tooltip: 'Team Analyser',
                icon: <ContactCardRibbon20Regular />,
            },
            {
                label: 'Security Governance',
                link: '/security-governance',
                tooltip: 'Security Governance',
                icon: <PeopleSettings20Regular />,
            },
            {
                label: 'Software Inventory',
                link: '/software-inventory',
                tooltip: 'Software Inventory',
                icon: <ListBar20Regular />,
            },
        ],
        [],
    );

    const handleChangeSidebar = val => {
        setExpandSidebar(val);
    };

    // Check if a feature is enabled for the current project
    const isFeatureEnabled = route => {
        const featureKey = routeToFeatureKey[route];
        if (!featureKey) return true; // If route not in mapping, allow it (backward compatibility)

        // Required features are always enabled
        if (REQUIRED_FEATURES.includes(featureKey)) {
            return true;
        }

        const enabledFeatures = selectedProject?.enabled_features;
        return isProjectFeatureEnabled(enabledFeatures, featureKey);
    };

    const currentScreenLabel = React.useMemo(() => {
        if (!location?.pathname || !links?.length) return '';
        const flatLinks = links.flatMap(item => (item?.subItems?.length ? item.subItems : [item]));
        const match = flatLinks
            .filter(item => item?.link && location.pathname.startsWith(item.link))
            .sort((a, b) => b.link.length - a.link.length)[0];
        return match?.label || '';
    }, [location?.pathname, links]);

    React.useEffect(() => {
        // 1) Start from the full set of possible sidebar items
        const allSidebarItems = [
            {
                label: 'Project Overview',
                link: '/overview',
                tooltip: 'Project Overview',
                icon: <Home20Regular />,
            },
            {
                label: 'VDR',
                link: '/dataroom',
                tooltip: 'VDR',
                icon: <DocumentFolder20Regular />,
            },
            {
                label: 'Observations',
                link: '/observations',
                tooltip: 'Observations',
                icon: <TableSearch20Regular />,
            },
            {
                label: 'Info Requests',
                link: '/irl',
                tooltip: 'Info Requests',
                icon: <ChatBubblesQuestion20Regular />,
            },
            {
                label: 'Lens Intelligence',
                link: '/lens-intelligence',
                tooltip: 'Lens Intelligence',
                icon: <Sparkle20Regular />,
            },
            {
                label: 'Project Mgmt.',
                link: '/tasks',
                tooltip: 'Project Management',
                icon: <GridKanban20Regular />,
            },
            {
                label: 'RAID Log',
                link: '/raid',
                tooltip: 'RAID Log',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M4.2113 6.4482V5.07404H5.80939C6.01297 5.07404 6.18941 5.03672 6.3387 4.96208C6.49477 4.88743 6.61353 4.78225 6.69496 4.64653C6.78318 4.51081 6.82729 4.35812 6.82729 4.18848C6.82729 4.02561 6.78657 3.88311 6.70514 3.76096C6.63049 3.63881 6.52531 3.54381 6.38959 3.47595C6.25387 3.40809 6.0978 3.37416 5.92136 3.37416H4.2113V2H5.92136C6.43709 2 6.89175 2.08822 7.28534 2.26465C7.68571 2.4343 7.99787 2.68199 8.2218 3.00772C8.44574 3.33344 8.55771 3.72703 8.55771 4.18848C8.55771 4.4667 8.48985 4.74153 8.35413 5.01297C8.2252 5.27762 8.03519 5.51853 7.78411 5.73568C7.53981 5.95283 7.24123 6.12587 6.88836 6.2548C6.54228 6.38374 6.14869 6.4482 5.7076 6.4482H4.2113ZM3 9.10491V2H4.67953V9.10491H3ZM6.868 9.10491C6.868 8.80633 6.81372 8.50435 6.70514 8.19898C6.59656 7.88683 6.43709 7.59164 6.22673 7.31341C6.02315 7.0284 5.76868 6.77054 5.46331 6.53981C5.15794 6.30909 4.80507 6.12248 4.4047 5.97997L6.26744 5.60335C6.66103 5.80014 7.00372 6.03426 7.29552 6.3057C7.58732 6.57714 7.82822 6.86893 8.01822 7.18109C8.21502 7.49324 8.36092 7.81558 8.45592 8.14809C8.55092 8.47381 8.59842 8.79276 8.59842 9.10491H6.868Z"
                            fill="#1B2149"
                        />
                        <path
                            d="M11.3366 7.74093V6.36677H15.5202V7.74093H11.3366ZM12.0593 2H14.5735L16.3752 9.10491H14.6651L13.7388 5.07404L13.3113 3.18076L12.8736 5.07404L11.927 9.10491H10.2678L12.0593 2Z"
                            fill="#1B2149"
                        />
                        <path
                            d="M4.95944 18.0001V10.8951H6.63897V18.0001H4.95944ZM3.33081 10.8951H8.2676V12.2693H3.33081V10.8951ZM3.33081 16.6259H8.2676V18.0001H3.33081V16.6259Z"
                            fill="#1B2149"
                        />
                        <path
                            d="M10.4053 18.0001V10.8951H12.7973C13.3334 10.8951 13.8118 10.98 14.2326 11.1496C14.6601 11.3125 15.0231 11.55 15.3217 11.8621C15.6203 12.1675 15.8476 12.5373 16.0037 12.9716C16.1598 13.406 16.2378 13.8911 16.2378 14.4272C16.2378 14.9701 16.1598 15.4621 16.0037 15.9032C15.8476 16.3375 15.6203 16.7141 15.3217 17.0331C15.0231 17.3452 14.6601 17.5861 14.2326 17.7558C13.8118 17.9186 13.3334 18.0001 12.7973 18.0001H10.4053ZM12.0848 16.6259H12.7973C13.1638 16.6259 13.4725 16.5377 13.7236 16.3612C13.9815 16.178 14.1749 15.9202 14.3038 15.5876C14.4395 15.2551 14.5074 14.8683 14.5074 14.4272C14.5074 13.9862 14.4395 13.6061 14.3038 13.2872C14.1749 12.9615 13.9815 12.7104 13.7236 12.534C13.4725 12.3575 13.1638 12.2693 12.7973 12.2693H12.0848V16.6259Z"
                            fill="#1B2149"
                        />
                    </svg>
                ),
            },
            {
                label: 'Action Log',
                link: '/action-log',
                tooltip: 'Action Log',
                icon: <ListBarTreeOffset20Regular />,
            },
            {
                label: 'Lessons Learnt',
                link: '/lessons',
                tooltip: 'Lessons Learnt',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <g clip-path="url(#clip0_5071_465)">
                            <path
                                d="M7.78097 15.9979C7.55891 15.9148 7.32016 15.8605 7.11814 15.7432C6.60642 15.4456 6.33678 14.9803 6.28669 14.3913C6.2796 14.3057 6.25622 14.2557 6.17942 14.2077C5.83632 13.9923 5.66185 13.6759 5.65643 13.2702C5.65309 13.0097 5.65225 12.7493 5.65768 12.4892C5.65977 12.3987 5.63347 12.3519 5.54582 12.3152C4.87882 12.0347 4.43555 11.5459 4.20097 10.8618C4.18094 10.8034 4.1225 10.7395 4.06574 10.7157C2.88284 10.2111 2.17368 9.11001 2.22294 7.82736C2.24297 7.30227 2.39949 6.81476 2.68917 6.3744C2.74927 6.28299 2.75219 6.22122 2.69084 6.12689C1.68992 4.57835 2.34607 2.49472 4.04862 1.79057C4.1104 1.76511 4.17927 1.70125 4.20014 1.63989C4.75193 0.0362615 6.60558 -0.511361 7.93457 0.541309C7.9546 0.55717 7.97506 0.571779 8.00219 0.592231C8.31857 0.321759 8.67461 0.139358 9.07948 0.0512876C10.2244 -0.197897 11.4148 0.479117 11.7784 1.59607C11.8118 1.69875 11.8598 1.75885 11.962 1.80142C13.66 2.50849 14.3053 4.58086 13.3089 6.12605C13.2484 6.21997 13.2501 6.28132 13.3098 6.37357C14.3019 7.91083 13.6604 9.98529 11.975 10.6919C11.871 10.7358 11.8151 10.7938 11.7771 10.904C11.5496 11.5643 11.1101 12.0351 10.4686 12.3106C10.3709 12.3523 10.3375 12.3995 10.3413 12.5047C10.3496 12.7597 10.3467 13.0152 10.3434 13.2702C10.3375 13.6755 10.1643 13.9927 9.82036 14.2077C9.74356 14.2557 9.71893 14.3053 9.71351 14.3913C9.66217 15.1793 9.07948 15.8284 8.30522 15.9682C8.27558 15.9737 8.24762 15.9874 8.21923 15.9974H7.7818L7.78097 15.9979ZM8.47885 5.07881V11.5572C8.88414 11.5572 9.28067 11.5793 9.67385 11.5526C10.4874 11.4975 11.0838 10.7061 10.9419 9.90139C10.8906 9.60963 10.7482 9.36921 10.5512 9.15175C10.3651 8.94681 10.383 8.65171 10.5771 8.48016C10.767 8.31237 11.0575 8.32197 11.2362 8.52107C11.3676 8.66715 11.4962 8.82284 11.5897 8.99439C11.707 9.21019 11.7871 9.44643 11.8873 9.68351C11.9215 9.66222 11.9658 9.6376 12.0063 9.60796C12.9608 8.92177 13.124 7.54728 12.3577 6.65615C12.0876 6.34226 12.086 6.15778 12.3506 5.85099C13.1019 4.97905 12.9784 3.66092 12.0776 2.94467C12.0146 2.89458 11.9441 2.85284 11.866 2.79942C11.8451 2.87329 11.8301 2.92714 11.8147 2.98098C11.7045 3.36958 11.5087 3.7085 11.2232 3.994C11.065 4.15261 10.8359 4.17891 10.6489 4.06913C10.469 3.96353 10.3738 3.74774 10.4431 3.54447C10.4769 3.44554 10.5475 3.35372 10.6176 3.27316C11.1193 2.6959 11.0738 1.84108 10.5086 1.31808C9.946 0.79759 9.09868 0.812199 8.56817 1.35899C8.51391 1.41492 8.47384 1.51008 8.47301 1.5873C8.46633 2.37868 8.46883 3.17006 8.46925 3.96186C8.46925 4.00694 8.47468 4.05202 8.4751 4.05953C8.77562 4.18684 9.06821 4.27574 9.32408 4.42642C9.94641 4.79289 10.3179 5.33342 10.3417 6.07513C10.3505 6.3431 10.1484 6.55054 9.89299 6.56098C9.63838 6.57141 9.42717 6.37607 9.40672 6.11144C9.36665 5.59012 9.00101 5.18191 8.47885 5.07839V5.07881ZM4.13711 2.80067C4.08827 2.82905 4.06073 2.84324 4.03527 2.86036C3.04145 3.5428 2.86155 4.93815 3.64876 5.85016C3.91005 6.15318 3.9113 6.34477 3.65251 6.64362C2.86656 7.55187 3.03602 8.93637 4.01815 9.62466C4.05321 9.64928 4.09328 9.66681 4.13461 9.69019C4.27986 9.15968 4.44807 8.83202 4.75318 8.53275C4.82999 8.45762 4.94101 8.3971 5.04578 8.37456C5.2503 8.33115 5.43521 8.43759 5.52578 8.61498C5.62304 8.80573 5.58171 8.99982 5.40766 9.19892C5.05538 9.60212 4.92891 10.0646 5.10588 10.5759C5.28787 11.1022 5.65935 11.4549 6.21824 11.5301C6.60767 11.5822 7.00753 11.5534 7.40281 11.5597C7.44246 11.5601 7.48211 11.5534 7.53011 11.5488C7.53011 10.5196 7.53095 9.49944 7.52594 8.47974C7.52594 8.44427 7.47001 8.39209 7.42869 8.37581C7.22583 8.29692 7.00753 8.24976 6.81637 8.15042C6.1452 7.80148 5.71737 7.26972 5.65977 6.49127C5.63639 6.17489 5.82255 5.95242 6.09928 5.93697C6.37476 5.92195 6.57553 6.12689 6.5939 6.44202C6.62019 6.89949 7.06221 7.36113 7.52385 7.41288C7.52594 7.39744 7.53011 7.382 7.53011 7.36697C7.53011 5.42984 7.53137 3.49313 7.52761 1.556C7.52761 1.49673 7.49547 1.42452 7.45415 1.38027C7.20162 1.1098 6.88607 0.962043 6.5171 0.941174C5.95027 0.908617 5.4206 1.2321 5.16933 1.75593C4.91931 2.27684 5.00988 2.85117 5.41392 3.30572C5.62429 3.54238 5.6293 3.82996 5.42644 4.01403C5.22359 4.19811 4.93517 4.16638 4.72188 3.93807C4.42428 3.61918 4.23812 3.24185 4.13669 2.80025L4.13711 2.80067ZM6.59431 12.5059C6.59431 12.7676 6.59097 13.021 6.59598 13.2744C6.59807 13.3887 6.67404 13.4363 6.78381 13.4359C6.93449 13.4355 7.08559 13.4359 7.23627 13.4359C7.88615 13.4359 8.53604 13.4351 9.18592 13.4367C9.30362 13.4367 9.40046 13.4088 9.4038 13.2735C9.41006 13.0206 9.40547 12.7672 9.40547 12.5064H6.59431V12.5059ZM8.75934 14.3796H7.2371C7.28009 14.7774 7.6161 15.0675 8.01721 15.06C8.40456 15.0525 8.73221 14.754 8.75934 14.3796Z"
                                fill="#1B2149"
                            />
                            <path
                                d="M0 1.90663C0.0154436 1.88117 0.0321394 1.85655 0.0459134 1.83025C0.184071 1.57021 0.472908 1.48256 0.722511 1.64075C1.04307 1.84402 1.35862 2.05564 1.67 2.27269C1.89163 2.42713 1.93755 2.70636 1.79146 2.92007C1.64996 3.1271 1.37615 3.19012 1.15618 3.04988C0.831868 2.84285 0.510891 2.62956 0.197845 2.40626C0.114784 2.3474 0.0651136 2.24097 0 2.15665C0 2.07317 0 1.99011 0 1.90663Z"
                                fill="#1B2149"
                            />
                            <path
                                d="M0 10.3426C0.121045 10.2199 0.22915 10.0805 0.365638 9.97822C0.606892 9.79749 0.86359 9.63804 1.11611 9.47317C1.36488 9.3108 1.63869 9.35547 1.79146 9.57835C1.94631 9.805 1.88287 10.0838 1.63285 10.2541C1.3749 10.4298 1.11403 10.601 0.854407 10.7742C0.462891 11.0355 0.232489 10.9866 0 10.5926C0 10.5091 0 10.4261 0 10.3426Z"
                                fill="#1B2149"
                            />
                            <path
                                d="M0 6.1244C0.124384 5.85476 0.337255 5.75876 0.628597 5.77754C0.876947 5.7934 1.12738 5.77921 1.37699 5.7813C1.66582 5.7838 1.87452 5.98207 1.87452 6.2492C1.87452 6.51675 1.66624 6.71459 1.37699 6.71752C1.12738 6.72002 0.876947 6.70541 0.628597 6.72127C0.337673 6.74006 0.124801 6.64364 0 6.37442V6.1244Z"
                                fill="#1B2149"
                            />
                            <path
                                d="M14.5937 9.35583C14.6838 9.3909 14.7832 9.41218 14.8633 9.46352C15.1697 9.65886 15.4723 9.8613 15.7724 10.0667C16.0111 10.2303 16.0679 10.512 15.9147 10.7332C15.764 10.9511 15.4927 11 15.2531 10.8438C14.9438 10.6427 14.6366 10.4386 14.3328 10.2294C14.147 10.1017 14.084 9.89845 14.1516 9.69518C14.2147 9.5061 14.3887 9.37796 14.5937 9.35583Z"
                                fill="#1B2149"
                            />
                            <path
                                d="M15.9998 2.02932C15.9923 2.18918 15.9314 2.32317 15.7974 2.41416C15.4835 2.62703 15.17 2.84074 14.8503 3.04484C14.6216 3.19093 14.3465 3.12916 14.2042 2.91503C14.061 2.70007 14.1111 2.42251 14.3332 2.2689C14.645 2.05311 14.9614 1.84358 15.2798 1.63739C15.4363 1.53596 15.6046 1.53471 15.7673 1.62946C15.9193 1.71753 15.9898 1.8561 15.9998 2.0289V2.02932Z"
                                fill="#1B2149"
                            />
                            <path
                                d="M15.0467 6.71786C14.9014 6.71786 14.7558 6.71995 14.6105 6.71744C14.3338 6.71202 14.1276 6.51375 14.1255 6.2533C14.123 5.99285 14.3288 5.78582 14.603 5.78206C14.9094 5.77789 15.2157 5.77789 15.5221 5.78206C15.7968 5.78582 16.0017 5.99243 15.9996 6.2533C15.9971 6.51459 15.7922 6.71243 15.5146 6.71744C15.3589 6.72037 15.2032 6.71786 15.0471 6.71786H15.0467Z"
                                fill="#1B2149"
                            />
                        </g>
                        <defs>
                            <clipPath id="clip0_5071_465">
                                <rect width="16" height="15.9979" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                ),
            },
            {
                label: 'Journal',
                link: '/journal',
                tooltip: 'Journal',
                icon: <NotepadEdit20Regular />,
            },
            {
                label: 'The Tent',
                link: '/tent',
                tooltip: 'The Tent',
                icon: <PeopleTeam20Regular />,
            },
        ];

        const enabledPrimaryItems = allSidebarItems.filter(item => {
            if (!item?.link) return true;
            return isFeatureEnabled(item.link);
        });

        const enabledMoreItems = moreSidebarItems.filter(item => isFeatureEnabled(item.link));

        let sidebarItems = [...enabledPrimaryItems];

        if (user?.has_diligence_tools && enabledMoreItems.length > 0) {
            sidebarItems.push({
                label: 'More...',
                tooltip: 'More...',
                icon: <Apps20Regular />,
                subItems: enabledMoreItems,
            });
        }

        // If user does NOT have diligence tools, restrict to the VDR subset
        if (!user?.has_diligence_tools) {
            const vdrAllowedRoutes = ['/overview', '/dataroom', '/wizard', '/irl'];
            const vdrItems = sidebarItems.filter(item => item?.link && vdrAllowedRoutes.includes(item.link));
            const wizardItem = enabledMoreItems.find(item => item.link === '/wizard');

            if (wizardItem && !vdrItems.some(item => item.link === '/wizard')) {
                vdrItems.push(wizardItem);
            }

            setLinks(vdrItems);
            return;
        }

        setLinks(sidebarItems);
    }, [selectedProject, user, moreSidebarItems]);

    return (
        <div className={styles.sidebarBody} style={{ width: expandSidebar ? 188 : 48 }}>
            <div style={{ width: expandSidebar && 188 }} className={styles.menuBody + ' d-flex justify-content-between'}>
                {expandSidebar && (
                    <span onClick={() => handleChangeSidebar(false)} className={styles.menuLabel + ' ' + styles.currentScreenText}>
                        {currentScreenLabel}
                    </span>
                )}
                {expandSidebar ? <ChevronLeft20Regular onClick={() => handleChangeSidebar(false)} /> : <ChevronRight20Regular onClick={() => handleChangeSidebar(true)} />}
            </div>

            <div className={styles.menuList}>
                {links?.map((item, index) => {
                const isSubMenu = Boolean(item?.subItems?.length);
                const isActive = isSubMenu
                    ? item.subItems.some(subItem => location?.pathname?.includes(subItem?.link))
                    : location?.pathname?.includes(item?.link);
                const activeColor = isActive ? 'var(--brand-secondary, #8E2DE2)' : undefined;

                if (isSubMenu) {
                    return (
                        <div
                            key={`${item.label}-${index}`}
                            className={styles.menuItemWrapper}
                            onMouseEnter={openMoreMenu}
                            onMouseLeave={closeMoreMenu}>
                            <div
                                className={`${styles.menuBody} ${styles.menuBodyMore}`}
                                style={{ width: expandSidebar ? 188 : 48, color: activeColor }}>
                                <Tooltip positioning="after" withArrow content={item?.tooltip}>
                                    {item?.icon}
                                </Tooltip>
                                {expandSidebar && (
                                    <Text as="span" className={styles.menuLabel} style={{ color: activeColor }}>
                                        {item?.label}
                                    </Text>
                                )}
                                {moreMenuOpen && (
                                    <div className={styles.subMenu}>
                                        {item.subItems.map(subItem => {
                                            const isSubItemActive = location?.pathname?.includes(subItem?.link);

                                            return (
                                                <div
                                                    key={subItem.link}
                                                    onClick={() => {
                                                        setMoreMenuOpen(false);
                                                        navigate(subItem.link);
                                                    }}
                                                    className={`${styles.subMenuItem} ${isSubItemActive ? styles.subMenuItemActive : ''}`}
                                                    style={{ color: isSubItemActive ? 'var(--brand-secondary, #8E2DE2)' : undefined }}>
                                                    {subItem.icon}
                                                    <span className={styles.subMenuLabel}>{subItem.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                }

                return (
                    <div
                        key={item.link}
                        onClick={() => navigate(item?.link)}
                        className={styles.menuBody}
                        style={{ width: expandSidebar ? 188 : 48, color: activeColor }}>
                        <Tooltip positioning="after" withArrow content={item?.tooltip}>
                            {item?.icon}
                        </Tooltip>
                        {expandSidebar && (
                            <Text as="span" className={styles.menuLabel} style={{ color: activeColor }}>
                                {item?.label}
                            </Text>
                        )}
                    </div>
                );
                })}
            </div>
        </div>
    );
};

export default Sidebar;
